import {
  Button,
  Center,
  Divider,
  Group,
  Loader,
  PinInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandWindows,
  IconInfoCircle,
  IconKey,
  IconShieldCheck,
  IconShieldLock,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BrowserLoginSuccess,
  decodeNostrPublicKey,
  LOGIN_METHOD,
  type LoginChallenge,
  type LoginChallengeResult,
  type LoginChallengeUserResponse,
  type LoginRequest,
  type Verify2FALoginRequest,
} from '@/api';

interface SocialButton {
  key: LOGIN_METHOD;
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

interface StepOneProps {
  googleLogin: () => void;
  githubLogin: () => void;
  microsoftLogin: () => void;
  nostrLogin: () => void;
  oidcLogin: () => void;
  magicLinkLoginChallenge: (email: string) => void;
  loginMethods?: LOGIN_METHOD[];
  isBusy: boolean;
  t: TFunction<'translation', undefined>;
}

const StepOne = ({
  googleLogin,
  githubLogin,
  microsoftLogin,
  nostrLogin,
  oidcLogin,
  magicLinkLoginChallenge,
  loginMethods,
  isBusy,
  t,
}: StepOneProps) => {
  const formRequestEmailMagicLink = useForm({
    initialValues: {
      email: '',
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : t('login.invalidEmail')),
    },
  });

  const hasEmailMagicLinkLogin = loginMethods?.includes(LOGIN_METHOD.EMAIL_MAGIC_LINK);

  const socialButtons: SocialButton[] = [];
  if (loginMethods?.includes(LOGIN_METHOD.GOOGLE)) {
    socialButtons.push({
      key: LOGIN_METHOD.GOOGLE,
      label: t('login.googleTab'),
      icon: <IconBrandGoogle size={20} />,
      onClick: googleLogin,
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.GITHUB)) {
    socialButtons.push({
      key: LOGIN_METHOD.GITHUB,
      label: t('login.githubTab'),
      icon: <IconBrandGithub size={20} />,
      onClick: githubLogin,
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.MICROSOFT)) {
    socialButtons.push({
      key: LOGIN_METHOD.MICROSOFT,
      label: t('login.microsoftTab'),
      icon: <IconBrandWindows size={20} />,
      onClick: microsoftLogin,
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.NOSTR)) {
    socialButtons.push({
      key: LOGIN_METHOD.NOSTR,
      label: t('login.nostrTab'),
      icon: <IconKey size={20} />,
      onClick: nostrLogin,
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.OIDC)) {
    socialButtons.push({
      key: LOGIN_METHOD.OIDC,
      label: t('login.oidcSsoTab'),
      icon: <IconShieldCheck size={20} />,
      onClick: oidcLogin,
    });
  }

  const cols = Math.min(Math.max(socialButtons.length, 1), 3);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={3} fw={600}>
          {t('login.welcomeBack')}
        </Title>
        <Text size="sm" c="dimmed">
          {t('login.cardSubtitle')}
        </Text>
      </Stack>

      {socialButtons.length > 0 && (
        <SimpleGrid cols={cols} spacing="sm">
          {socialButtons.map((btn) => (
            <Tooltip key={btn.key} label={btn.label} withArrow>
              <Button
                variant="default"
                size="md"
                px="sm"
                onClick={btn.onClick}
                loading={isBusy}
                aria-label={btn.label}
              >
                {btn.icon}
              </Button>
            </Tooltip>
          ))}
        </SimpleGrid>
      )}

      {socialButtons.length > 0 && hasEmailMagicLinkLogin && (
        <Divider label={t('login.orDivider')} labelPosition="center" />
      )}

      {hasEmailMagicLinkLogin && (
        <form
          onSubmit={formRequestEmailMagicLink.onSubmit(() =>
            magicLinkLoginChallenge(formRequestEmailMagicLink.values.email)
          )}
        >
          <Stack gap="sm">
            <TextInput
              label={t('login.emailLabel')}
              placeholder={t('login.emailPlaceholder')}
              type="email"
              autoComplete="email"
              {...formRequestEmailMagicLink.getInputProps('email')}
            />
            <Button type="submit" loading={isBusy} fullWidth size="md">
              {t('login.loginWithEmail')}
            </Button>
            <Group gap="xs" wrap="nowrap" align="center">
              <IconInfoCircle size={16} style={{ flexShrink: 0 }} />
              <Text size="sm" c="dimmed">
                {t('login.magicLinkInfo')}
              </Text>
            </Group>
          </Stack>
        </form>
      )}
    </Stack>
  );
};

interface StepTwoProps {
  loginMethod: LOGIN_METHOD;
  setActiveStep: (step: number) => void;
  t: TFunction<'translation', undefined>;
}

const StepTwo = ({ loginMethod, setActiveStep, t }: StepTwoProps) => {
  return (
    <>
      {loginMethod === LOGIN_METHOD.EMAIL_MAGIC_LINK && (
        <>
          <Stack gap="md">
            <Text size="lg" fw={500}>
              {t('login.emailSent')}
            </Text>
            <Text>{t('login.clickLinkToLogin')}</Text>
            <Text size="sm" c="dimmed">
              {t('login.wrongEmail')}
            </Text>
            <Button variant="outline" onClick={() => setActiveStep(0)}>
              {t('login.back')}
            </Button>
          </Stack>
        </>
      )}
      {loginMethod === LOGIN_METHOD.NOSTR && (
        <>
          <Text mb="md">{t('login.nostrInstructions')}</Text>
          <Center>
            <Loader size="xl" />
          </Center>
        </>
      )}
      {loginMethod === LOGIN_METHOD.GOOGLE && (
        <>
          <Text mb="md">{t('login.redirectingGoogle')}</Text>
          <Center>
            <Loader size="xl" />
          </Center>
        </>
      )}
      {loginMethod === LOGIN_METHOD.GITHUB && (
        <>
          <Text mb="md">{t('login.redirectingGithub')}</Text>
          <Center>
            <Loader size="xl" />
          </Center>
        </>
      )}
      {loginMethod === LOGIN_METHOD.MICROSOFT && (
        <>
          <Text mb="md">{t('login.redirectingMicrosoft')}</Text>
          <Center>
            <Loader size="xl" />
          </Center>
        </>
      )}
      {loginMethod === LOGIN_METHOD.OIDC && (
        <>
          <Text mb="md">{t('login.redirectingOidc')}</Text>
          <Center>
            <Loader size="xl" />
          </Center>
        </>
      )}
    </>
  );
};

interface StepTwoFactorProps {
  pendingToken: string;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  loginDoneCb: () => void;
  setActiveStep: (step: number) => void;
  setHasError: (error: string | null) => void;
  t: TFunction<'translation', undefined>;
}

const StepTwoFactor = ({
  pendingToken,
  verify2FA,
  loginDoneCb,
  setActiveStep,
  setHasError,
  t,
}: StepTwoFactorProps) => {
  const [code, setCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const handleVerify = async () => {
    if (code.length < 6) return;

    setIsBusy(true);
    setHasError(null);

    try {
      await verify2FA({
        pending_token: pendingToken,
        code: code,
      });
      loginDoneCb();
    } catch (e: any) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else if (e?.response?.status === 401) {
        setHasError(t('login.invalid2FACode'));
      } else if (e?.response?.status === 429) {
        setHasError(t('login.tooMany2FAAttempts'));
        setActiveStep(0);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
      setCode('');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Stack gap="md" align="center">
      <IconShieldLock size={48} stroke={1.5} />
      <Text size="lg" fw={500}>
        {t('login.twoFactorRequired')}
      </Text>
      <Text size="sm" c="dimmed" ta="center">
        {t('login.enterTwoFactorCode')}
      </Text>
      <PinInput
        length={6}
        type="number"
        value={code}
        onChange={setCode}
        oneTimeCode
        disabled={isBusy}
        size="lg"
      />
      <Button onClick={handleVerify} loading={isBusy} disabled={code.length < 6} fullWidth>
        {t('login.verify')}
      </Button>
      <Text size="sm" c="dimmed" ta="center">
        {t('login.useRecoveryCode')}
      </Text>
      <Button variant="subtle" size="xs" onClick={() => setActiveStep(0)}>
        {t('login.back')}
      </Button>
    </Stack>
  );
};

export interface LoginProps {
  login: (loginRequest: LoginRequest) => Promise<LoginChallenge>;
  loginChallenge: (props: LoginChallengeUserResponse) => Promise<LoginChallengeResult>;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  loginDoneCb: () => void;
  loginMethods?: LOGIN_METHOD[];
}

export function Login(props: LoginProps) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [loginMethod, setLoginMethod] = useState<LOGIN_METHOD>(LOGIN_METHOD.EMAIL_MAGIC_LINK);
  const [isBusy, setIsBusy] = useState(false);
  const [hasError, setHasError] = useState<string | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  const nostrLogin = async () => {
    setLoginMethod(LOGIN_METHOD.NOSTR);
    setHasError(null);
    if (!(window as any).nostr) {
      setHasError(t('login.nostrNoExtension'));
      setActiveStep(0);
      return;
    }
    setIsBusy(true);
    setActiveStep(1);
    try {
      const publicKey = await (window as any).nostr.getPublicKey();
      if (!publicKey) {
        setHasError(t('login.nostrNoPublicKey'));
        return;
      }
      const challenge = await props.login({
        type: 'NOSTR',
        content: {
          public_key: decodeNostrPublicKey(publicKey),
        },
      });
      if (challenge?.type !== 'NOSTR') {
        setHasError(t('login.nostrNoChallenge'));
        return;
      }
      const loginResult = await props.loginChallenge({
        type: 'NOSTR',
        content: {
          id: (challenge.content as any).id,
          response: await (window as any).nostr.signEvent({
            kind: 22242,
            created_at: Math.floor(Date.now() / 1000),
            tags: [['challenge', (challenge.content as any).challenge]],
            content: '',
          }),
        },
      });
      if (!loginResult) {
        setHasError(t('login.nostrNoChallengeResponse'));
        return;
      }

      if (loginResult.status === 'requires_2fa') {
        setPendingToken(loginResult.data.pending_token);
        setActiveStep(2);
        return;
      }

      props.loginDoneCb();
    } catch (e: any) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.error) {
        // The shared challenge errors are worded for magic links; use a
        // Nostr-appropriate message instead of the raw backend text.
        switch (errorData.error) {
          case 'challenge_mismatch':
          case 'invalid_challenge':
            setHasError(t('login.nostrChallengeFailed'));
            break;
          default:
            setHasError(errorData.message || `Error: ${e?.message || e}`);
        }
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const googleLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.GOOGLE);
    setHasError(null);
    setActiveStep(1);
    try {
      const googleChallenge = await props.login({
        type: LOGIN_METHOD.GOOGLE,
        content: {},
      });
      if (!googleChallenge || googleChallenge.type !== LOGIN_METHOD.GOOGLE) {
        setHasError(t('login.googleNoChallenge'));
        return;
      }
      window.location.href = (googleChallenge.content as any).auth_url;
    } catch (e: any) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const githubLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.GITHUB);
    setHasError(null);
    setActiveStep(1);
    try {
      const githubChallenge = await props.login({
        type: LOGIN_METHOD.GITHUB,
        content: {},
      });
      if (!githubChallenge || githubChallenge.type !== LOGIN_METHOD.GITHUB) {
        setHasError(t('login.githubNoChallenge'));
        return;
      }
      window.location.href = (githubChallenge.content as any).auth_url;
    } catch (e: any) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const oidcLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.OIDC);
    setHasError(null);
    setActiveStep(1);
    try {
      const oidcChallenge = await props.login({
        type: LOGIN_METHOD.OIDC,
        content: {},
      });
      if (!oidcChallenge || oidcChallenge.type !== LOGIN_METHOD.OIDC) {
        setHasError(t('login.oidcNoChallenge'));
        return;
      }
      window.location.href = (oidcChallenge.content as any).auth_url;
    } catch (e: any) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const microsoftLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.MICROSOFT);
    setHasError(null);
    setActiveStep(1);
    try {
      const microsoftChallenge = await props.login({
        type: LOGIN_METHOD.MICROSOFT,
        content: {},
      });
      if (!microsoftChallenge || microsoftChallenge.type !== LOGIN_METHOD.MICROSOFT) {
        setHasError(t('login.microsoftNoChallenge'));
        return;
      }
      window.location.href = (microsoftChallenge.content as any).auth_url;
    } catch (e: any) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const magicLinkLoginChallenge = async (email: string) => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.EMAIL_MAGIC_LINK);
    setHasError(null);
    try {
      const emailMagicLinkChallenge = await props.login({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          email: email,
        },
      });
      if (
        !emailMagicLinkChallenge ||
        emailMagicLinkChallenge.type !== LOGIN_METHOD.EMAIL_MAGIC_LINK
      ) {
        setHasError(t('login.emailNoChallenge'));
        return;
      }
      setActiveStep(1);
    } catch (e: any) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const ErrorMessage = () => (
    <Text c="red" mb="md">
      {hasError}
    </Text>
  );

  return (
    <>
      {activeStep === 0 && (
        <StepOne
          googleLogin={googleLogin}
          githubLogin={githubLogin}
          microsoftLogin={microsoftLogin}
          nostrLogin={nostrLogin}
          oidcLogin={oidcLogin}
          magicLinkLoginChallenge={magicLinkLoginChallenge}
          loginMethods={props.loginMethods}
          isBusy={isBusy}
          t={t}
        />
      )}
      {activeStep === 1 && (
        <StepTwo loginMethod={loginMethod} setActiveStep={setActiveStep} t={t} />
      )}
      {activeStep === 2 && pendingToken && (
        <StepTwoFactor
          pendingToken={pendingToken}
          verify2FA={props.verify2FA}
          loginDoneCb={props.loginDoneCb}
          setActiveStep={setActiveStep}
          setHasError={setHasError}
          t={t}
        />
      )}
      <ErrorMessage />
    </>
  );
}
